<template>
  <!-- PAGE TRANSITION -->
  <transition name="page-fade" appear>
    <div v-if="!pageLoading">

      <!-- HERO -->
      <PageHero
        :title="$t('hero.title_developers')"
        :breadcrumbsTop="[
          { label: $t('hero.breadcrumbs.home'), link: '/' },
          { label: $t('hero.breadcrumbs.developers') }
        ]"
        :backgroundImageUrl="heroBackground"
        :loaded="true"
      />

      <!-- CONTENT -->
      <div class="container py-10">

        <MiniFilterBar />

        <div class="row mt-4">
          <div
            v-for="developer in developers"
            :key="developer.id"
            class="col-md-4 mb-4"
          >
            <div class="card border-0 shadow-sm h-100 rounded-5 overflow-hidden">

              <!-- IMAGE -->
              <div class="image-wrapper">
                <img
                  :src="developer.logo || '/images/no-image.png'"
                  class="card-img-top lazy-img"
                  :alt="$t('developer.logo_alt')"
                  loading="lazy"
                  @load="onImageLoad"
                />
              </div>

              <!-- BODY -->
              <div class="card-body d-flex flex-column">
                <h3 class="h5 mb-2">
                  <router-link
                    :to="`/public/developers/${developer.id}`"
                    class="text-decoration-none text-dark"
                  >
                    {{ developer.name }}
                  </router-link>
                </h3>

                <ul class="list-inline text-muted small mb-2">
                  <li v-if="developer.country" class="list-inline-item">🌍 {{ developer.country }}</li>
                  <li v-if="developer.established_year" class="list-inline-item">
                    🏗 {{ $t('developer.founded') }}: {{ developer.established_year }}
                  </li>
                  <li class="list-inline-item">🏢 {{ developer.residential_complexes_count }}</li>
                  <li class="list-inline-item">🏠 {{ developer.buildings_count }}</li>
                  <li class="list-inline-item">🛏 {{ developer.properties_count }}</li>
                </ul>

                <p class="small text-muted line-clamp-3 mb-3">
                  {{ developer.description }}
                </p>

                <router-link
                  :to="`/public/developers/${developer.id}`"
                  class="mt-auto btn secondary-solid-btn btn-sm"
                >
                  {{ $t('developer.buttons.more') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- EMPTY -->
        <div v-if="developers.length === 0" class="text-center text-muted py-5">
          {{ $t('developer.no_available') }}
        </div>

        <!-- PAGINATION -->
        <div
          v-if="pagination.total > pagination.per_page"
          class="mt-5 d-flex justify-content-center"
        >
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="previousPage">
                  {{ $t('developer.buttons.pagination.prev') }}
                </a>
              </li>
              <li class="page-item disabled">
                <span class="page-link">{{ currentPage }} / {{ totalPages }}</span>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage">
                  {{ $t('developer.buttons.pagination.next') }}
                </a>
              </li>
            </ul>
          </nav>
        </div>

      </div>
    </div>
  </transition>

  <!-- SKELETON -->
  <div v-if="pageLoading" class="page-skeleton">
    <div class="skeleton hero-skeleton"></div>
    <div class="container mt-4">
      <div class="row">
        <div v-for="i in 6" :key="i" class="col-md-4 mb-4">
          <div class="skeleton card-skeleton"></div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useLanguageStore } from '@/stores/languageStore'
import { useFilterStore } from '@/stores/filterStore'

import PageHero from '../components/PageHero.vue'
import MiniFilterBar from '../components/MiniFilterBar.vue'

/* GLOBAL PAGE STATE */
const pageLoading = ref(true)

/* STORES */
const { locale } = storeToRefs(useLanguageStore())
const {
  country_id, region_id, city_id, district_id
} = storeToRefs(useFilterStore())

/* HERO */
const heroBackground = ref(null)

/* DEVELOPERS */
const developers = ref([])
const pagination = ref({ page: 1, per_page: 25, total: 0 })

const currentPage = computed(() => pagination.value.page)
const totalPages = computed(() =>
  Math.ceil(pagination.value.total / pagination.value.per_page)
)

/* LOAD HERO */
async function loadHeroBanner () {
  const res = await fetch(
    `/api/v1/banners?placement=developers`,
    {
      headers: { 'X-Locale': (locale.value || 'ru').split('-')[0] },
      cache: 'no-store'
    }
  )

  const banner = (await res.json())?.[0]
  if (!banner?.image_url) return

  const img = new Image()
  img.src = banner.image_url
  await img.decode()

  heroBackground.value = banner.image_url
}

/* LOAD DEVELOPERS */
async function fetchDevelopers () {
  const params = new URLSearchParams({
    country_id: country_id.value || '',
    region_id: region_id.value || '',
    city_id: city_id.value || '',
    district_id: district_id.value || '',
    page: pagination.value.page,
    per_page: pagination.value.per_page,
    locale: (locale.value || 'ru').split('-')[0]
  })

  const res = await fetch(`/api/v1/developers?${params}`, { cache: 'no-store' })
  const data = await res.json()

  developers.value = data.developers || []
  pagination.value.total = data.pagination?.total || 0
}

/* RELOAD PAGE (KEY POINT) */
async function reloadPage () {
  pageLoading.value = true
  await Promise.all([
    loadHeroBanner(),
    fetchDevelopers()
  ])
  pageLoading.value = false
}

/* PAGINATION */
function nextPage () {
  if (pagination.value.page < totalPages.value) {
    pagination.value.page++
    reloadPage()
  }
}

function previousPage () {
  if (pagination.value.page > 1) {
    pagination.value.page--
    reloadPage()
  }
}

/* IMAGE FADE */
function onImageLoad (e) {
  e.target.classList.add('loaded')
}

/* INIT */
onMounted(reloadPage)
watch(locale, reloadPage)
watch([country_id, region_id, city_id, district_id], reloadPage)

</script>

<style scoped>
/* PAGE TRANSITION */
.page-fade-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* IMAGES */
.image-wrapper {
  height: 220px;
  background: #f3f4f6;
}
.lazy-img {
  opacity: 0;
  transition: opacity 0.5s ease;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.lazy-img.loaded {
  opacity: 1;
}

/* SKELETON */
.skeleton {
  background: linear-gradient(90deg, #eee, #f5f5f5, #eee);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  border-radius: 12px;
}
.hero-skeleton {
  height: 516px;
}
.card-skeleton {
  height: 340px;
}
@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
}

</style>
